const parseResponse = async (response) => {
  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    const requestError = new Error(
      payload?.error?.message || payload?.error || `HTTP ${response.status}`,
    );
    requestError.code = payload?.error?.code;
    requestError.status = response.status;
    requestError.details = payload?.error?.details;
    throw requestError;
  }

  return payload;
};

export const uploadToPresignedUrl = ({
  file,
  headers,
  method,
  onProgress,
  url,
}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method || "PUT", url);

    Object.entries(headers || {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === "function") {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload S3 non riuscito: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Errore di rete durante l'upload"));
    xhr.send(file);
  });

export const createInternalDriveApi = ({ baseUrl, getTokenSilently }) => {
  const request = async (path, { method = "GET", query, body } = {}) => {
    const token = await getTokenSilently();
    const url = new URL(`${baseUrl}${path}`);

    Object.entries(query || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });

    const response = await fetch(url.toString(), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return parseResponse(response);
  };

  return {
    async listItems(prefix = "", continuationToken) {
      const payload = await request("/internal-drive/items", {
        query: { prefix, continuationToken },
      });
      return payload.data;
    },

    async createFolder(path, name) {
      const payload = await request("/internal-drive/folders", {
        method: "POST",
        body: { path, name },
      });
      return payload.result;
    },

    async createUpload({
      path,
      filename,
      contentType,
      contentLength,
      overwrite,
    }) {
      const payload = await request("/internal-drive/uploads", {
        method: "POST",
        body: {
          path,
          filename,
          contentType,
          contentLength,
          overwrite,
        },
      });
      return payload.result;
    },

    async deleteItem(key, type) {
      const payload = await request("/internal-drive/items", {
        method: "DELETE",
        body: { key, type },
      });
      return payload.result;
    },

    async moveItem({ sourceKey, destinationKey, type, overwrite = false }) {
      const payload = await request("/internal-drive/items", {
        method: "PATCH",
        body: { sourceKey, destinationKey, type, overwrite },
      });
      return payload.result;
    },

    async getAccess(key, mode = "view") {
      const payload = await request("/internal-drive/access", {
        query: { key, mode },
      });
      return payload.data;
    },
  };
};

export { parseResponse };
